import { router } from 'react-query-kit'
import Debug from 'debug'
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js'
import {
  callUnaryMethod,
  createConnectQueryKey,
} from '@connectrpc/connect-query'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import type {
  BusinessUser,
  CreateBusinessUserRequest,
  PatchBusinessRequest,
  PatchBusinessUserRequest,
  RemoveBusinessUserRequest,
  TerminateBusinessRequest,
} from '@jotta/grpc-connect-openapi/business'
import {
  createBusinessUser,
  listUsers,
  patchBusiness,
  patchBusinessUser,
  removeBusinessUser,
  terminateBusiness,
} from '@jotta/grpc-connect-openapi/businessQuery'
import { getConfig } from '@jotta/grpc-connect-openapi/configQuery'
import { AccountType } from '@jotta/grpc-connect-openapi/customer'
import { getCustomer } from '@jotta/grpc-connect-openapi/customerQuery'
import type { GetOrderRequest } from '@jotta/grpc-connect-openapi/payment'
import type { CheckEmailRequest } from '@jotta/grpc-connect-openapi/signup'
import { checkEmail } from '@jotta/grpc-connect-openapi/signupQuery'
import {
  changePayment,
  createStripeIntent,
  getOrder,
  getPaymentInfo,
  getSupportedPaymentMethods,
  listOrders,
} from '@jotta/grpc-connect-openapi/subscriptionQuery'
import { queryClient } from '@jotta/query'
import type { PartialMessage, MessageData } from '@jotta/types/compat'

const debug = Debug('jotta:biz:api')
export type ExtendedBusinessUser = MessageData<BusinessUser> & {
  isCurrentUser: boolean
}
export const bizApi = router('biz', {
  subscription: {
    getPaymentInfo: router.query({
      fetcher: async () => {
        const paymentInfo = await callUnaryMethod(
          authTransport,
          getPaymentInfo,
          {},
        )
        return paymentInfo
      },
    }),
    getBillingHistory: router.query({
      fetcher: async () => {
        const data = await callUnaryMethod(authTransport, listOrders, {})
        return data.orders
      },
    }),
    getOrder: router.query({
      fetcher: async (args: PartialMessage<GetOrderRequest>) => {
        const { order } = await callUnaryMethod(authTransport, getOrder, args)
        if (!order) {
          throw new Error('Order undefined')
        }
        return order
      },
    }),
    changePayment: router.mutation({
      mutationFn: async ({
        stripe,
        cardNumberElement,
      }: {
        cardNumberElement: StripeCardNumberElement | null
        stripe: Stripe
      }) => {
        const log = debug.extend('onVerify')
        log('Verifying payment method')
        if (!cardNumberElement) {
          throw new Error('CardNumberElement not found')
        }
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
        })
        if (error) {
          throw error
        }
        const { clientSecret } = await callUnaryMethod(
          authTransport,
          createStripeIntent,
          { paymentMethod: paymentMethod.id },
        )
        const confirmCardSetupResponse = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardNumberElement,
            },
          },
        )
        if (confirmCardSetupResponse.error) {
          throw confirmCardSetupResponse.error
        }
        if (confirmCardSetupResponse?.setupIntent.payment_method) {
          await callUnaryMethod(authTransport, changePayment, {
            paymentMethod: paymentMethod.id,
          })
          return {
            success: true,
          }
        }
        throw new Error(
          'Payment method not found in stripe.confirmCardSetup response',
        )
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: bizApi.subscription.getPaymentInfo.getKey(),
        })
      },
    }),
  },
  config: {
    get: router.query({
      fetcher: async () => {
        const config = await callUnaryMethod(authTransport, getConfig, {})
        return config
      },
    }),
  },
  company: {
    get: router.query({
      fetcher: async () => {
        const { customer } = await callUnaryMethod(
          authTransport,
          getCustomer,
          {},
        )
        if (!customer) {
          throw new Error('Customer not found')
        }
        if (customer.accountType !== AccountType.BUSINESS_ADMIN) {
          throw new Error('Access denied')
        }
        const company = customer.business
        if (!company) {
          throw new Error('customer.business undefined')
        }
        return {
          username: customer.username,
          company,
          customer,
        }
      },
    }),
    update: router.mutation({
      mutationFn: async (args: PartialMessage<PatchBusinessRequest>) => {
        await callUnaryMethod(authTransport, patchBusiness, args)
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: bizApi.company.get.getKey(),
        })
        await queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getSupportedPaymentMethods,
            input: {},
            transport: authTransport,
            cardinality: 'finite',
          }),
        })
      },
    }),
    terminate: router.mutation({
      mutationFn: async (args: PartialMessage<TerminateBusinessRequest>) => {
        return await callUnaryMethod(authTransport, terminateBusiness, args)
      },
      onSuccess: async (data, variables) => {
        debug('Removed user', data, variables)
        await queryClient.invalidateQueries({
          queryKey: bizApi.company.getKey(),
        })
      },
    }),
  },

  user: {
    byId: router.query({
      fetcher: async ({
        username,
      }: {
        username: string
      }): Promise<ExtendedBusinessUser> => {
        const { users } = await callUnaryMethod(authTransport, listUsers, {})
        const user = users.find(u => u.username === username)
        if (!user) {
          throw new Error('User not found')
        }
        return {
          ...user,
          isCurrentUser: true,
        }
      },
    }),
    list: router.query({
      fetcher: async ({ username }: { username?: string } = {}, context) => {
        const { users } = await callUnaryMethod(authTransport, listUsers, {})
        return users.map<ExtendedBusinessUser>(user => ({
          ...user,
          isCurrentUser: user.username === username,
        }))
      },
    }),
    checkEmal: router.mutation({
      mutationFn: async (args: PartialMessage<CheckEmailRequest>) => {
        const response = await callUnaryMethod(authTransport, checkEmail, args)
        return response
      },
    }),
    patch: router.mutation({
      mutationFn: async (args: PartialMessage<PatchBusinessUserRequest>) => {
        await callUnaryMethod(authTransport, patchBusinessUser, args)
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: bizApi.company.get.getKey(),
        })
      },
    }),
    create: router.mutation({
      mutationFn: async (args: PartialMessage<CreateBusinessUserRequest>) => {
        await callUnaryMethod(authTransport, createBusinessUser, args)
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: bizApi.user.getKey(),
        })
      },
    }),
    remove: router.mutation({
      mutationFn: async (args: PartialMessage<RemoveBusinessUserRequest>) => {
        const response = await callUnaryMethod(
          authTransport,
          removeBusinessUser,
          args,
        )
        return response
      },
      onSuccess: async (data, variables) => {
        debug('Removed user', data, variables)
        await queryClient.invalidateQueries({
          queryKey: bizApi.user.getKey(),
        })
      },
    }),
  },
})

export function useBizUsers() {
  const { data } = bizApi.company.get.useSuspenseQuery()
  const { data: users } = bizApi.user.list.useSuspenseQuery({
    variables: { username: data.username },
  })
  const maxUserCount = Number(data.company.maxUserCount)
  return {
    users,
    maxUserCount,
    canCreateUser: maxUserCount < 0 || users.length < maxUserCount,
  }
}
